import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from './supabase'
import { useAuth } from './auth-context'
import { getTenantSlug } from './tenant-config'

export interface Workspace {
  id: string
  name: string
  slug: string
  type: 'personal' | 'organization'
  role: 'owner' | 'admin' | 'user'
  logoUrl?: string
  primaryColor?: string
}

interface WorkspaceContextType {
  workspaces: Workspace[]
  activeWorkspace: Workspace | null
  setActiveWorkspace: (workspace: Workspace) => void
  isLoading: boolean
  isPersonal: boolean
  isAdmin: boolean
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null)

const ACTIVE_WORKSPACE_KEY = `lead-magnifier-${getTenantSlug()}-active-workspace`

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ACTIVE_WORKSPACE_KEY)
    }
    return null
  })

  // Fetch all workspaces for the user
  const { data: workspaces = [], isLoading } = useQuery({
    queryKey: ['workspaces', user?.id],
    queryFn: async (): Promise<Workspace[]> => {
      if (!user) return []

      const { data, error } = await supabase
        .from('tenant_users')
        .select(`
          role,
          tenants (
            id,
            name,
            slug,
            tenant_type,
            logo_url,
            primary_color
          )
        `)
        .eq('user_id', user.id)

      if (error) {
        console.error('Error fetching workspaces:', error)
        return []
      }

      return (data || []).map((tu) => {
        const tenant = tu.tenants as {
          id: string
          name: string
          slug: string
          tenant_type: 'personal' | 'organization'
          logo_url?: string
          primary_color?: string
        }
        return {
          id: tenant.id,
          name: tenant.name,
          slug: tenant.slug,
          type: tenant.tenant_type,
          role: tu.role as Workspace['role'],
          logoUrl: tenant.logo_url,
          primaryColor: tenant.primary_color,
        }
      }).sort((a, b) => {
        // Personal workspace first, then alphabetically
        if (a.type === 'personal' && b.type !== 'personal') return -1
        if (a.type !== 'personal' && b.type === 'personal') return 1
        return a.name.localeCompare(b.name)
      })
    },
    enabled: !!user,
  })

  // Determine active workspace
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0] || null

  // Update localStorage when active workspace changes
  useEffect(() => {
    if (activeWorkspace) {
      localStorage.setItem(ACTIVE_WORKSPACE_KEY, activeWorkspace.id)
    }
  }, [activeWorkspace])

  // Set active workspace if none selected but workspaces exist
  useEffect(() => {
    if (!activeWorkspaceId && workspaces.length > 0) {
      setActiveWorkspaceId(workspaces[0].id)
    }
  }, [workspaces, activeWorkspaceId])

  const setActiveWorkspace = (workspace: Workspace) => {
    setActiveWorkspaceId(workspace.id)
    // Invalidate all workspace-dependent queries
    queryClient.invalidateQueries({ queryKey: ['events'] })
    queryClient.invalidateQueries({ queryKey: ['leads'] })
    queryClient.invalidateQueries({ queryKey: ['lead-stats'] })
    queryClient.invalidateQueries({ queryKey: ['team'] })
  }

  const isPersonal = activeWorkspace?.type === 'personal'
  const isAdmin = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin'

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        activeWorkspace,
        setActiveWorkspace,
        isLoading,
        isPersonal,
        isAdmin,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider')
  }
  return context
}
